import React from "react";
import { motion } from "framer-motion";
import { Layers, Info, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { fmtUSD, blendEstimates } from "../../lib/format";

const PortalFindButton = ({ icon: Icon, label, onClick, disabled, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} disabled={disabled}
    className="mb-2 inline-flex items-center gap-1.5 rounded-md border border-brand-hairline bg-white px-2.5 py-1.5 text-[0.7rem] font-semibold text-brand-emerald hover:border-brand-emerald transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
    <Icon className="h-3 w-3" /> {label}
  </button>
);

const Field = ({ label, testid, value, onChange }) => (
  <div>
    <label className="text-xs font-medium text-brand-stone" htmlFor={testid}>{label}</label>
    <div className="mt-1 flex items-center rounded-lg border border-brand-hairline bg-white focus-within:ring-1 focus-within:ring-brand-emerald">
      <span className="pl-3 text-brand-stone">$</span>
      <input id={testid} data-testid={testid} inputMode="numeric" placeholder="Optional"
        value={value === "" ? "" : Number(String(value).replace(/[^0-9.]/g, "")).toLocaleString("en-US")}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
        className="w-full bg-transparent px-2 py-2.5 font-mono text-sm outline-none text-brand-ink" />
    </div>
  </div>
);

export default function BlendedEstimate({ primary, panel, owner, setOwner, onUseValue, liveZestimate, address }) {
  const blend = blendEstimates(primary, owner, panel);
  const zLive = !!(liveZestimate && liveZestimate.value);

  const currentAddress = React.useMemo(() => {
    if (address?.normalized) return address.normalized;
    const parts = [address?.line1, address?.city, address?.state, address?.zip].filter(Boolean).join(", ");
    if (parts) return parts;
    try { return new URLSearchParams(window.location.search).get("address") || ""; } catch { return ""; }
  }, [address]);

  const copyAndOpen = (url) => {
    if (!currentAddress) return;
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(currentAddress)
          .then(() => toast.success("Address copied to clipboard! Paste it into the search bar."))
          .catch(() => {});
      }
    } catch (e) { /* clipboard unavailable */ }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
      className="rounded-2xl bg-white border border-brand-hairline p-5 sm:p-8 shadow-[0_4px_20px_rgba(11,30,140,0.04)]" data-testid="blended-estimate-card">
      <div className="flex items-center gap-2">
        <Layers className="h-5 w-5 text-brand-gold" />
        <span className="eyebrow">Portal Comparison</span>
      </div>
      <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-brand-ink">Compare against the major portals</h2>
      <p className="mt-2 text-sm text-brand-stone max-w-2xl">
        Zillow's Zestimate® is auto-detected live where available. Redfin and Homes.com are <strong>optional</strong> — type them in only if you'd like to compare. They never block or delay your instant valuation.
      </p>

      <div className="mt-6 grid sm:grid-cols-3 gap-4" data-testid="portal-comparison-grid">
        <div className="rounded-xl border border-brand-hairline bg-brand-sand/40 p-4" data-testid="portal-zillow">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-brand-ink">Zillow Zestimate®</span>
            {zLive ? (
              <span className="rounded-full bg-brand-emerald text-brand-cream px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider" data-testid="zillow-live-badge">Live · Auto-detected</span>
            ) : (
              <span className="rounded-full bg-brand-hairline text-brand-stone px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider">Optional manual entry</span>
            )}
          </div>
          {zLive ? (
            <div className="mt-3">
              <p className="font-mono text-xl font-bold text-brand-emerald" data-testid="zillow-live-value">{fmtUSD(liveZestimate.value)}</p>
              {(liveZestimate.low || liveZestimate.high) && (
                <p className="mt-0.5 text-xs text-brand-stone font-mono">{fmtUSD(liveZestimate.low)} – {fmtUSD(liveZestimate.high)}</p>
              )}
              <p className="mt-1 text-[0.65rem] text-brand-stone/70">via Bridge Interactive</p>
            </div>
          ) : (
            <div className="mt-3">
              <Field label="Enter Zillow value" testid="owner-zestimate-input" value={owner.zestimate}
                onChange={(v) => setOwner((p) => ({ ...p, zestimate: v, zestimateLive: false }))} />
            </div>
          )}
        </div>

        <div className="rounded-xl border border-brand-hairline bg-brand-sand/40 p-4" data-testid="portal-redfin">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-brand-ink">Redfin Estimate</span>
            <span className="rounded-full bg-brand-hairline text-brand-stone px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider">Optional manual entry</span>
          </div>
          <div className="mt-3">
            <PortalFindButton icon={ExternalLink} label="Copy Address & Open Redfin" onClick={() => copyAndOpen("https://www.redfin.com")} disabled={!currentAddress} testid="redfin-find-button" />
            <p className="mb-2 text-[0.65rem] text-brand-stone/70">Copies address so you can paste it on their site.</p>
            <Field label="Enter Redfin value" testid="owner-redfin-input" value={owner.redfin} onChange={(v) => setOwner((p) => ({ ...p, redfin: v }))} />
          </div>
        </div>

        <div className="rounded-xl border border-brand-hairline bg-brand-sand/40 p-4" data-testid="portal-homescom">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-brand-ink">Homes.com Estimate</span>
            <span className="rounded-full bg-brand-hairline text-brand-stone px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider">Optional manual entry</span>
          </div>
          <div className="mt-3">
            <PortalFindButton icon={ExternalLink} label="Copy Address & Open Homes.com" onClick={() => copyAndOpen("https://www.homes.com")} disabled={!currentAddress} testid="homescom-find-button" />
            <p className="mb-2 text-[0.65rem] text-brand-stone/70">Copies address so you can paste it on their site.</p>
            <Field label="Enter Homes.com value" testid="owner-homescom-input" value={owner.homescom} onChange={(v) => setOwner((p) => ({ ...p, homescom: v }))} />
          </div>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6 items-center min-w-0">
        <div className="space-y-2 min-w-0">
          {blend.sources.map((s, i) => (
            <div key={i} className="flex items-center justify-between gap-3 rounded-lg bg-brand-sand px-4 py-2.5" data-testid="blend-source-row">
              <span className="text-sm text-brand-stone flex items-center gap-2 min-w-0">
                <span className="truncate">{s.label}</span>
                {s.owner && <span className="rounded-full bg-brand-gold/15 text-brand-gold px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider shrink-0">Owner</span>}
              </span>
              <span className="flex items-center gap-3 shrink-0">
                <span className="font-mono text-sm font-medium text-brand-ink">{fmtUSD(s.value)}</span>
                <button onClick={() => onUseValue(s.value, s.label)} data-testid={`use-source-${i}-button`}
                  className="rounded-md border border-brand-hairline bg-white px-2 py-1 text-[0.65rem] font-semibold text-brand-emerald hover:border-brand-emerald transition-colors">
                  Use
                </button>
              </span>
            </div>
          ))}
        </div>
        <div className="rounded-xl bg-brand-emerald text-brand-cream p-5 sm:p-6 min-w-0">
          <span className="text-xs uppercase tracking-[0.2em] text-brand-cream/60">
            {blend.blended ? "Blended (simple average of all sources)" : "AI Panel baseline"}
          </span>
          <p className="mt-2 font-mono text-3xl font-bold text-brand-gold" data-testid="blended-estimate-amount">{fmtUSD(blend.point)}</p>
          <p className="mt-1 text-xs text-brand-cream/60 font-mono break-words">{fmtUSD(blend.low)} – {fmtUSD(blend.high)}</p>
          <button onClick={() => onUseValue(blend.point, blend.blended ? "Blended value" : "AI Panel baseline")} data-testid="use-blended-value-button"
            className="mt-4 w-full rounded-lg bg-brand-gold px-4 py-2.5 text-sm font-semibold text-brand-dark hover:brightness-95 transition-all">
            Use in net sheet
          </button>
        </div>
      </div>

      {blend.blended && (
        <div className="mt-4 flex items-start gap-2 rounded-xl bg-brand-goldMuted p-3">
          <Info className="h-4 w-4 text-brand-gold shrink-0 mt-0.5" />
          <p className="text-xs text-brand-stone leading-relaxed">
            Zillow's Zestimate® (when shown live) is provided via Bridge Interactive. Redfin and Homes.com figures are optional values you enter and are not independently verified. A simple average is not an appraisal or guaranteed sale price.
          </p>
        </div>
      )}
    </motion.div>
  );
}
